/*
 * Dónde cae cada evento en la pantalla (sin React).
 *   · Día completo y eventos de 24 h o más → la franja superior (bandas que cruzan días).
 *   · El resto → la rejilla de horas, partido por medianoche si cruza al día siguiente; los que se solapan van
 *     uno al lado del otro (columnas dentro de cada grupo de solapes, como Google).
 */
import { diffDias, minutosDe, sumarDias, ymdDe } from '../../lib/calendario';

const DIA_MS = 86400000;

/** Prepara las ocurrencias para pintarlas en la zona `tz`. */
export function prepararEventos(eventos, tz) {
  return eventos.map((ev) => {
    if (ev.todo_el_dia) {
      const dIni = ev.fecha_inicio || ymdDe(ev.inicio, tz);
      const dFin = ev.fecha_fin || sumarDias(dIni, 1);
      return { ev, banda: true, dIni, dFin, segmentos: [] };
    }
    const ini = new Date(ev.inicio).getTime();
    const fin = new Date(ev.fin).getTime();
    const dIni = ymdDe(ini, tz);
    const dUltimo = ymdDe(fin - 1, tz);
    if (fin - ini >= DIA_MS) {
      return { ev, banda: true, dIni, dFin: sumarDias(dUltimo, 1), segmentos: [] };
    }
    const segmentos = [];
    const mIni = minutosDe(ini, tz);
    const mFin = minutosDe(fin, tz);
    if (dIni === dUltimo) {
      segmentos.push({ ymd: dIni, m0: mIni, m1: mFin === 0 ? 1440 : Math.max(mFin, mIni), primero: true, ultimo: true });
    } else {
      const n = diffDias(dIni, dUltimo);
      for (let i = 0; i <= n; i += 1) {
        const ymd = sumarDias(dIni, i);
        segmentos.push({
          ymd,
          m0: i === 0 ? mIni : 0,
          m1: i === n ? (mFin === 0 ? 1440 : mFin) : 1440,
          primero: i === 0,
          ultimo: i === n,
        });
      }
    }
    return { ev, banda: false, dIni, dFin: sumarDias(dUltimo, 1), segmentos };
  });
}

/** Bandas de la franja superior para los días [dias[0] … dias[n-1]] → filas sin pisarse. */
export function disponerBandas(preparados, dias) {
  const d0 = dias[0];
  const n = dias.length;
  const bandas = [];
  for (const p of preparados) {
    if (!p.banda) continue;
    const a = Math.max(0, diffDias(d0, p.dIni));
    const b = Math.min(n, diffDias(d0, p.dFin));
    if (b <= a) continue;
    bandas.push({ ...p, col: a, span: b - a, cortadaIzq: p.dIni < d0, cortadaDer: diffDias(d0, p.dFin) > n });
  }
  bandas.sort((x, y) => x.col - y.col || y.span - x.span);
  const filas = [];
  for (const bnd of bandas) {
    let fila = filas.findIndex((ocupadas) => ocupadas.every((o) => o.col + o.span <= bnd.col || bnd.col + bnd.span <= o.col));
    if (fila === -1) { filas.push([]); fila = filas.length - 1; }
    filas[fila].push(bnd);
    bnd.fila = fila;
  }
  return { bandas, filas: filas.length };
}

/** Segmentos con hora de UN día, con columna y nº de columnas de su grupo de solapes. */
export function disponerDia(preparados, ymd, minimo = 22) {
  const segs = [];
  for (const p of preparados) {
    if (p.banda) continue;
    for (const s of p.segmentos) if (s.ymd === ymd) segs.push({ ...s, ev: p.ev, fin: Math.max(s.m1, s.m0 + minimo) });
  }
  segs.sort((a, b) => a.m0 - b.m0 || b.fin - a.fin);
  const grupos = [];
  let actual = [];
  let finGrupo = -1;
  for (const s of segs) {
    if (actual.length && s.m0 >= finGrupo) { grupos.push(actual); actual = []; finGrupo = -1; }
    actual.push(s);
    finGrupo = Math.max(finGrupo, s.fin);
  }
  if (actual.length) grupos.push(actual);
  for (const g of grupos) {
    const cols = [];
    for (const s of g) {
      let i = cols.findIndex((f) => f <= s.m0);
      if (i === -1) { cols.push(0); i = cols.length - 1; }
      cols[i] = s.fin;
      s.col = i;
    }
    for (const s of g) s.ncols = cols.length;
  }
  return segs;
}

/** Eventos (bandas y con hora) que tocan un día, ordenados como en la vista de mes y la agenda. */
export function eventosDelDia(preparados, ymd) {
  return preparados
    .filter((p) => p.dIni <= ymd && ymd < p.dFin)
    .sort((a, b) => (b.banda - a.banda) || (new Date(a.ev.inicio) - new Date(b.ev.inicio)) || String(a.ev.titulo).localeCompare(String(b.ev.titulo)));
}

/** Colores de un bloque según la respuesta y la disponibilidad (como Google: pendiente = contorno, no = tachado). */
export function estiloEvento(ev, color, ahora = Date.now()) {
  const pasado = new Date(ev.fin).getTime() < ahora;
  const base = { '--ev': color, opacity: pasado ? 0.66 : 1 };
  if (ev.solo_ocupado) {
    return { ...base, background: `color-mix(in srgb, ${color} 22%, var(--panel-2))`, color: 'var(--text-2)', borderColor: 'transparent' };
  }
  if (ev.mi_estado === 'pendiente') {
    return { ...base, background: 'var(--panel-2)', color, borderColor: color, borderStyle: 'solid' };
  }
  if (ev.mi_estado === 'rechazado') {
    return { ...base, background: 'var(--panel-2)', color: 'var(--text-3)', borderColor: `color-mix(in srgb, ${color} 50%, transparent)`, textDecoration: 'line-through' };
  }
  if (ev.mi_estado === 'quizas') {
    return {
      ...base,
      background: `repeating-linear-gradient(135deg, color-mix(in srgb, ${color} 30%, var(--panel-2)) 0 6px, color-mix(in srgb, ${color} 18%, var(--panel-2)) 6px 12px)`,
      color: 'var(--text)', borderColor: color,
    };
  }
  if (ev.disponibilidad === 'libre') {
    return { ...base, background: `color-mix(in srgb, ${color} 28%, var(--panel-2))`, color: 'var(--text)', borderColor: color };
  }
  return { ...base, background: color, color: '#fff', borderColor: 'transparent' };
}
